import type { GameBoardOverlayInterface } from "@/game/context/GameBoardOverlayContext";
import { GameBoardOverlayContext } from "@/game/context/GameBoardOverlayContext";
import { GameLogger } from "@/game/utils/gameLogger.ts";
import { TweenManager } from "@/game/utils/tweenManager";
import * as PIXI from "pixi.js";
import React, { useEffect, useMemo, useRef, useState } from "react";

interface GameBoardOverlayProps {
  gameArea: DOMRect | null;
  gameHeaderArea: DOMRect | null;
  playArea?: { x: number; y: number; width: number; height: number };
  children?: React.ReactNode;
  setApiRef?: (api: GameBoardOverlayInterface | null) => void;
}

export function GameBoardOverlay({
  gameArea,
  gameHeaderArea,
  playArea: playAreaInitial,
  children,
  setApiRef
}: GameBoardOverlayProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pixiAppRef = useRef<PIXI.Application | null>(null);
  const playAreaContainer = useRef<PIXI.Container | null>(null);
  const sprites = useRef<Map<string, PIXI.Sprite>>(new Map());
  const spriteTweens = useRef<Map<string, any>>(new Map());
  const tweenManager = useRef(new TweenManager());
  // The desired play area, which can be updated by the context API
  const playAreaTarget = useRef(playAreaInitial);
  // The actual play area assigned to the container on render, which updates based on the target
  const [playAreaActual, setPlayAreaActual] = useState(playAreaInitial);

  // Interface with setPlayArea updating state
  const api = useMemo<GameBoardOverlayInterface>(
    () => ({
      addSpriteWithTween: (key, sprite, tweenOrGroup, toPlayArea = false) => {
        if (!pixiAppRef.current) {
          GameLogger.logInfo(
            "GameBoardOverlay",
            "addSpriteWithTween - Pixi app not initialized yet."
          );
          return;
        }
        if (sprites.current.has(key)) {
          GameLogger.logWarn("GameBoardOverlay", `Sprite with key '${key}' already exists.`);
        } else {
          sprites.current.set(key, sprite);
          if (toPlayArea && playAreaContainer.current) {
            // Adjust sprite position to be relative to the play area
            sprite.x -= playAreaContainer.current.x;
            sprite.y -= playAreaContainer.current.y - (gameHeaderArea ? gameHeaderArea.height : 0);

            playAreaContainer.current.addChild(sprite);
          } else {
            pixiAppRef.current.stage.addChild(sprite);
          }

          if (tweenOrGroup) {
            sprites.current.set(key, sprite);
            spriteTweens.current.set(key, tweenOrGroup);
            tweenManager.current.add(tweenOrGroup);
          }

          GameLogger.logDebug(
            "GameBoardOverlay",
            `Added sprite with key '${key} gameHeaderArea.height: ${gameHeaderArea ? gameHeaderArea.height : 0}'`
          );
        }
      },
      removeSprite: (key, delayMs?: number) => {
        const sprite = sprites.current.get(key);
        const tween = spriteTweens.current.get(key);

        // Remove from maps and tween manager immediately
        if (sprite) {
          sprites.current.delete(key);

          GameLogger.logDebug("GameBoardOverlay", `Removed sprite with key '${key}'`);
        }
        if (tween) {
          tweenManager.current.remove(tween);
          spriteTweens.current.delete(key);
        }

        // Remove from PIXI parent after delay
        const doRemove = () => {
          if (sprite && sprite.parent) {
            sprite.parent.removeChild(sprite);
          }
        };

        if (delayMs && delayMs > 0) {
          setTimeout(doRemove, delayMs);
        } else {
          doRemove();
        }
      },
      setPlayArea: (area) => {
        GameLogger.logDebug("GameBoardOverlay", `setPlayArea: ${JSON.stringify(area)}`);
        playAreaTarget.current = area;
      },
      get playArea() {
        return playAreaActual ?? { x: 0, y: 0, width: 0, height: 0 };
      },
      isPlayAreaValid() {
        if (!playAreaTarget.current || !playAreaTarget) {
          return false;
        }
        // Valid if the target and actual (container) play areas are in sync
        return !(
          playAreaTarget.current.x != playAreaActual?.x ||
          playAreaTarget.current.y != playAreaActual?.y ||
          playAreaTarget.current.width != playAreaActual?.width ||
          playAreaTarget.current.height != playAreaActual?.height
        );
      }
    }),
    [gameHeaderArea, playAreaActual]
  );

  // Create PIXI app
  useEffect(() => {
    if (!containerRef.current) return;

    const width = gameArea ? gameArea.width : 0;
    const height = gameArea ? gameArea.height : 0;

    GameLogger.logInfo(
      "GameBoardOverlay",
      `Initialize Pixi app overlay. width: ${width}, height: ${height}`
    );

    const app = new PIXI.Application({
      width: width,
      height: height,
      antialias: true,
      backgroundAlpha: 0
    });
    pixiAppRef.current = app;
    containerRef.current.appendChild(app.view as HTMLCanvasElement);

    app.ticker.add((deltaTime) => {
      tweenManager.current.update();
    });

    // Create new container for the play area
    const container = new PIXI.Container();
    container.name = "play-area-container";
    container.position.set(
      playAreaTarget.current ? playAreaTarget.current.x : 0,
      playAreaTarget.current ? playAreaTarget.current.y : 0
    );
    container.visible = true;
    container.alpha = 1;

    pixiAppRef.current.stage.addChild(container);
    playAreaContainer.current = container;

    return () => {
      tweenManager.current.clear();
      app.ticker.stop();
      app.destroy(true, { children: true });
      if (containerRef.current?.contains(app.view as HTMLCanvasElement)) {
        containerRef.current.removeChild(app.view as HTMLCanvasElement);
      }
    };
  }, []);

  // Update app and container properties on prop changes
  useEffect(() => {
    const width = gameArea ? gameArea.width : 0;
    const height = gameArea ? gameArea.height : 0;
    const headerHeight = gameHeaderArea ? gameHeaderArea.height : 0;

    if (pixiAppRef.current) {
      pixiAppRef.current.renderer.resize(width, height);
    }

    if (playAreaContainer.current && playAreaTarget.current) {
      GameLogger.logDebug(
        "GameBoardOverlay",
        `Updating play area container position. Width: ${width}, Height: ${height}, PlayArea:`,
        playAreaTarget.current
      );

      playAreaContainer.current.position.set(
        playAreaTarget.current.x,
        playAreaTarget.current.y + headerHeight
      );
      // Update the actual play area state now that the container has been updated
      setPlayAreaActual(playAreaTarget.current);

      const debugPlayArea = false;
      // Debug for making sure the play area is correctly positioned
      if (debugPlayArea) {
        // Remove existing debug rect if present
        const existingDebugRect = playAreaContainer.current?.getChildByName("debug-rect");
        if (existingDebugRect) {
          playAreaContainer.current?.removeChild(existingDebugRect);
          existingDebugRect.destroy();
        }

        const debugRect = new PIXI.Graphics();
        debugRect.name = "debug-rect";
        debugRect.lineStyle(3, 0xff00ff, 1);
        debugRect.drawRect(0, 0, playAreaTarget.current.width, playAreaTarget.current.height);
        playAreaContainer.current.addChild(debugRect);
      }
    }
  }, [gameArea, playAreaTarget]);

  useEffect(() => {
    if (setApiRef && api) {
      setApiRef(api);
    }
    return () => {
      if (setApiRef) setApiRef(null);
    };
  }, [setApiRef, api]);

  return (
    <GameBoardOverlayContext.Provider value={api}>
      <div
        id={"game-board-overlay"}
        ref={containerRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: gameArea ? gameArea.width : 0,
          height: gameArea ? gameArea.height : 0,
          pointerEvents: "none",
          zIndex: 100
        }}
      />
      {children}
    </GameBoardOverlayContext.Provider>
  );
}
